import React from "react";
import styled, { css } from "styled-components";
import { premiumTheme } from "./premiumTheme";

// Classification is presentation metadata, never a replacement validation rule.
export function normalizeRequirement(value) {
    if (value === "simple") return "optional";
    return ["mandatory", "recommended", "optional"].includes(value) ? value : null;
}

export function resolveFieldPresentation({ appearance = "legacy", requirement, interactionState, requiredType = "simple" } = {}) {
    const isPremium = appearance === "premium";
    const legacyInteraction = requiredType === "disabledSimple" ? "disabled" :
        ["locked", "mandatoryLocked"].includes(requiredType) ? "locked" : "enabled";
    const requested = isPremium && ["enabled", "locked", "disabled"].includes(interactionState) ? interactionState : "enabled";
    // Explicit state may add a restriction, but cannot lift any existing restriction.
    const effectiveInteraction = legacyInteraction === "disabled" || requested === "disabled" ? "disabled" :
        legacyInteraction === "locked" || requested === "locked" ? "locked" : "enabled";
    const inferred = normalizeRequirement(requiredType === "mandatoryLocked" ? "mandatory" : requiredType === "none" ? "optional" : requiredType);
    return { isPremium, requirement: isPremium ? (requirement === undefined ? inferred : normalizeRequirement(requirement)) : null,
        interactionState: effectiveInteraction, isBlocked: effectiveInteraction !== "enabled" };
}

// One source of truth for visible indicators and their legend entries.
export function resolveVisibleFieldStatus({ requirement, interactionState } = {}) {
    if (interactionState === "locked") return "locked";
    const normalized = normalizeRequirement(requirement);
    return ["mandatory", "recommended"].includes(normalized) ? normalized : null;
}

export function mergeDescriptionIds(...values) {
    return [...new Set(values.filter(Boolean).flatMap(value => String(value).split(/\s+/)).filter(Boolean))].join(" ") || undefined;
}

const Circle = styled.span`
    display: inline-block; width: ${premiumTheme.requirementIndicatorSize}; height: ${premiumTheme.requirementIndicatorSize};
    flex: 0 0 ${premiumTheme.requirementIndicatorSize}; margin-left: auto; align-self: center;
    box-sizing: border-box; padding: 0; border: 0;
    border-radius: 50%; background: ${({ $status }) => premiumTheme[$status]};
    pointer-events: none;
`;
const Description = styled.span`
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
`;

export function FieldRequirement({ requirement, interactionState = "enabled", descriptionId }) {
    const normalized = normalizeRequirement(requirement);
    const status = resolveVisibleFieldStatus({ requirement, interactionState });
    if (!status && !normalized) return null;
    const requirementText = normalized ? normalized[0].toUpperCase() + normalized.slice(1) : "";
    const description = status === "locked" ? [requirementText, "Locked"].filter(Boolean).join(", ") : requirementText;
    return <>
        {status && <Circle className="premium-requirement-indicator" data-requirement={normalized}
            data-status={status} $status={status} aria-hidden="true" />}
        <Description id={descriptionId}>{description}</Description>
    </>;
}

// Only the opt-in field receives these status rules. Legacy selectors and defaults remain intact.
export const premiumFieldPresentation = css`
    &&[data-appearance="premium"] {
        .Frame51 { display: flex; align-items: center; gap: 4px; min-width: 0; }
        .Title { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
        .Icon { width: ${premiumTheme.fieldIconSize}; height: ${premiumTheme.fieldIconSize}; flex-shrink: 0; }
        .premium-info-trigger { display: inline-flex; flex: 0 0 ${premiumTheme.fieldIconSize}; align-items: center; }
        .premium-info-trigger, .premium-info-trigger .Icon { cursor: help !important; }
        .premium-info-trigger:focus-visible { outline: 2px solid ${premiumTheme.mandatory}; outline-offset: 2px; }
        .Frame48 { box-sizing: border-box; border: 1px solid ${premiumTheme.fieldBorder} !important;
            border-radius: ${premiumTheme.fieldRadius}; background: ${premiumTheme.fieldBackground}; }
        .Frame48:focus-within { box-shadow: 0 0 0 2px ${premiumTheme.mandatory}; }
        .Frame48.invalid { border-color: ${premiumTheme.fieldError} !important; }
    }
    &&[data-appearance="premium"][data-interaction-state="locked"],
    &&[data-appearance="premium"][data-interaction-state="disabled"] {
        .Frame48, .Frame48Input, .dropdownArrow, .Frame48LeftContent, .Frame48RightContent { cursor: not-allowed !important; }
    }
`;
